import { useEffect, useState } from "react";
import { api } from "../lib/api.js";

type ReportType = "utilisation" | "versions" | "activations";

const REPORTS: { key: ReportType; label: string; blurb: string }[] = [
  { key: "utilisation", label: "Seat utilisation", blurb: "Active vs purchased seats per licence — spot over- and under-users." },
  { key: "versions", label: "Version adoption", blurb: "What app version the active fleet is running." },
  { key: "activations", label: "Activation trend", blurb: "New devices activated per day (last 30 days)." },
];

type Row = Record<string, string | number | null>;

function toCsv(rows: Row[]): string {
  if (rows.length === 0) return "";
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))];
  return lines.join("\r\n");
}

function download(filename: string, text: string) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function Reports() {
  const [active, setActive] = useState<ReportType>("utilisation");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ type: ReportType; rows: Row[] }>(`/admin/reports?type=${active}`)
      .then((res) => setRows(res.rows))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load report"))
      .finally(() => setLoading(false));
  }, [active]);

  const current = REPORTS.find((r) => r.key === active)!;
  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];

  return (
    <div>
      <div className="row-between">
        <h1>Reports</h1>
        <button
          className="secondary"
          onClick={() => download(`audioswytch-${active}.csv`, toCsv(rows))}
          disabled={rows.length === 0}
        >
          Download CSV
        </button>
      </div>

      <div className="button-row" style={{ margin: "6px 0 4px" }}>
        {REPORTS.map((r) => (
          <button
            key={r.key}
            className={r.key === active ? "primary" : "secondary"}
            onClick={() => setActive(r.key)}
          >
            {r.label}
          </button>
        ))}
      </div>
      <p className="muted-subtitle">{current.blurb}</p>

      {error && <p className="error-text">{error}</p>}
      {loading ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted-subtitle">No data yet.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={h}>{h.replace(/_/g, " ")}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {headers.map((h) => (
                  <td key={h}>{row[h] === null ? "—" : String(row[h])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
